<template>
  <div ref="dropdownContainer" class="relative">
    <!-- Bell Icon with Badge -->
    <StwButton
      text
      size="sm"
      class="hover:text-surface-950 !h-auto !min-h-0 !px-2"
      @click="toggle"
    >
      <i class="pi pi-bell" />
    </StwButton>
    <span
      v-if="badgeCount > 0"
      class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
    >
      {{ badgeCount > 99 ? '99+' : badgeCount }}
    </span>

    <!-- Custom Dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="bg-surface-100 absolute right-0 top-full z-50 mt-2 w-80 rounded-lg shadow-xl ring-1 [--tw-ring-color:var(--stw-glass-border)]"
      >
        <!-- Header -->
        <div
          class="border-surface-100 flex items-center justify-between border-b p-3"
        >
          <h3 class="text-surface-950 text-sm font-semibold">Notifications</h3>
          <!-- Always shown (disabled when nothing is unread), so it's easy to
               find when many notifications arrive. Invitations aren't affected:
               they stay until accepted or declined. -->
          <button
            class="text-primary-600 hover:text-primary-700 disabled:text-surface-400 text-xs disabled:cursor-default"
            :disabled="unreadCount === 0"
            @click="handleMarkAllRead"
          >
            Mark all read
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-surface-500 p-4 text-center text-sm">
          Loading...
        </div>

        <!-- Empty State -->
        <div
          v-else-if="invitations.length === 0 && notifications.length === 0"
          class="p-4 text-center text-sm"
        >
          <p class="text-surface-500 text-sm">No pending invitations</p>
        </div>

        <!-- List -->
        <div v-else class="max-h-96 overflow-y-auto">
          <!-- Invitations — pinned to the top, not affected by mark-as-read -->
          <div
            v-for="invitation in invitations"
            :key="invitation.id"
            class="border-surface-100 border-b p-3 last:border-b-0"
          >
            <div class="flex items-start gap-3">
              <img
                :src="invitation.inviter.avatar || PLATFORM_MARK_URL"
                :alt="invitation.inviter.username"
                class="h-8 w-8 flex-shrink-0 rounded-md object-cover ring-1 [--tw-ring-color:var(--stw-glass-border)]"
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm leading-tight">
                  <strong>{{ invitation.inviter.username }}</strong>
                  {{ formatInvitation(invitation).action }}
                </p>
                <p class="text-surface-500 mt-0.5 truncate text-xs">
                  {{ formatInvitation(invitation).name }}
                </p>
                <p class="text-surface-400 mt-0.5 text-xs">
                  {{ formatTimeAgo(invitation.created_at) }}
                </p>

                <div
                  v-if="!processingIds.has(invitation.id)"
                  class="mt-2 flex gap-2"
                >
                  <button
                    class="stw-brand-gradient rounded-md px-3 py-1 text-xs font-medium transition hover:brightness-110"
                    @click.stop="handleAccept(invitation)"
                  >
                    Accept
                  </button>
                  <button
                    class="hover:bg-surface-300 bg-surface-200 text-[var(--p-text-color)]/80 rounded-md px-3 py-1 text-xs font-medium transition"
                    @click.stop="handleDecline(invitation)"
                  >
                    Decline
                  </button>
                </div>
                <div v-else class="mt-2">
                  <span class="text-xs opacity-50">Processing...</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications -->
          <component
            :is="formatNotification(notification).href ? 'RouterLink' : 'div'"
            v-for="notification in notifications"
            :key="notification.id"
            :to="formatNotification(notification).href ?? undefined"
            class="border-surface-100 hover:bg-surface-100 block cursor-pointer border-b p-3 last:border-b-0"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex items-start gap-3">
              <i
                :class="formatNotification(notification).icon"
                class="text-surface-500 mt-0.5 flex-shrink-0 text-sm"
              />
              <div class="min-w-0 flex-1">
                <p
                  class="text-sm leading-tight"
                  :class="{ 'font-semibold': !notification.is_read }"
                >
                  {{ formatNotification(notification).text }}
                </p>
                <p class="text-surface-400 mt-0.5 text-xs">
                  {{ formatTimeAgo(notification.created_at) }}
                </p>
              </div>
              <span
                v-if="!notification.is_read"
                class="bg-primary-500 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
              />
            </div>
          </component>
        </div>

        <!-- See All -->
        <div class="border-surface-100 border-t p-2">
          <RouterLink
            to="/settings?tab=notifications"
            class="text-primary-600 hover:text-primary-700 block rounded-md p-2 text-center text-sm"
            @click="isOpen = false"
          >
            See all
          </RouterLink>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { NotificationResponse } from '@stw/types';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/notification';
import StwButton from '@/components/stw/StwButton.vue';
import { usePendingInvitations } from '@/composables/usePendingInvitations';
import { PLATFORM_MARK_URL } from '@/constants/branding';
import {
  formatInvitation,
  formatNotification,
} from '@/utils/notificationFormat';

const dropdownContainer = ref<HTMLElement>();
const isOpen = ref(false);

const loading = ref(false);
const unreadCount = ref(0);
const notifications = ref<NotificationResponse[]>([]);

const {
  invitations,
  processingIds,
  loadInvitations,
  handleAccept,
  handleDecline,
} = usePendingInvitations();

// getPendingInvitations() is unpaginated, so its length is always the true
// count — no separate count endpoint needed once the list has been fetched.
const badgeCount = computed(() => unreadCount.value + invitations.value.length);

const toggle = async () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await Promise.all([
      loadNotifications(),
      loadInvitations(),
      loadUnreadCount(),
    ]);
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    isOpen.value &&
    dropdownContainer.value &&
    !dropdownContainer.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
};

const loadNotifications = async () => {
  loading.value = true;
  try {
    const result = await getNotifications(1, 10);
    notifications.value = result.notifications;
  } catch (error) {
    console.error('Failed to load notifications:', error);
  } finally {
    loading.value = false;
  }
};

const loadUnreadCount = async () => {
  try {
    const result = await getUnreadNotificationCount();
    unreadCount.value = result.count;
  } catch (error) {
    console.error('Failed to load unread notification count:', error);
  }
};

const handleNotificationClick = async (notification: NotificationResponse) => {
  if (!notification.is_read) {
    notification.is_read = true;
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    try {
      await markNotificationRead(notification.id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }
  isOpen.value = false;
};

const handleMarkAllRead = async () => {
  notifications.value = notifications.value.map((n) => ({
    ...n,
    is_read: true,
  }));
  unreadCount.value = 0;
  try {
    await markAllNotificationsRead();
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
  }
};

const formatTimeAgo = (date: string | Date) => {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

onMounted(() => {
  // Fetch the real invitation list (not just a count) even before the
  // dropdown is opened, so its length can drive the badge immediately —
  // pending invites are always few, so this is cheap.
  loadInvitations();
  loadUnreadCount();
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
